import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { enrichPersonWithPolling } from 'src/app/utils/contactEnrich';
import { KTSVG } from 'src/_metronic/helpers';

const API = `${process.env.REACT_APP_API_URL}`;

// ─── Veri sağlayıcıları (Apollo / RocketReach) ───────────────────────────────
// Backend iki sağlayıcıyı da aynı IOrg/IPerson şekline map'ler; modal yalnızca
// /v1/apollo/* ↔ /v1/rocketreach/* önekini değiştirir.
type Provider = 'apollo' | 'rocketreach';
const PROVIDERS: { key: Provider; label: string; emoji: string }[] = [
  { key: 'apollo', label: 'Apollo.io', emoji: '🛰️' },
  { key: 'rocketreach', label: 'RocketReach', emoji: '🚀' },
];

interface Props {
  show: boolean;
  onClose: () => void;
}

interface IOrg {
  id: string;
  name: string;
  domain?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  linkedin_url?: string | null;
  industry?: string | null;
  estimated_num_employees?: number | null;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  founded_year?: number | null;
}

interface IPerson {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  title?: string | null;
  email?: string | null;
  email_locked: boolean;
  email_status?: string | null;
  photo_url?: string | null;
  linkedin_url?: string | null;
  city?: string | null;
  country?: string | null;
  seniority?: string | null;
  organization_name?: string | null;
  organization_phone?: string | null;
  obfuscated?: boolean;
  has_email?: boolean;
  has_direct_phone?: boolean;
}

interface IPagination {
  page: number;
  per_page?: number;
  total_entries: number;
  total_pages: number;
}

const TITLE_PRESETS: { label: string; emoji: string; titles: string[] }[] = [
  { label: 'Tümü', emoji: '👥', titles: [] },
  {
    label: 'Yönetim',
    emoji: '👑',
    titles: ['CEO', 'Founder', 'Co-Founder', 'Owner', 'General Manager', 'Genel Müdür', 'Managing Director', 'Chairman', 'Genel Müdür Yardımcısı', 'Yönetim Kurulu'],
  },
  {
    label: 'Bilgi Teknolojileri',
    emoji: '💻',
    titles: ['CTO', 'IT Manager', 'IT Director', 'Information Technology', 'Bilgi Teknolojileri', 'Bilgi İşlem', 'System Administrator', 'Sistem Yöneticisi', 'IT Specialist'],
  },
  {
    label: 'Satın Alma',
    emoji: '🛒',
    titles: ['Purchasing Manager', 'Procurement', 'Satın Alma', 'Satın Alma Müdürü', 'Satın Alma Uzmanı', 'Buyer', 'Supply Chain'],
  },
  {
    label: 'Finans',
    emoji: '💰',
    titles: ['CFO', 'Finance Manager', 'Finans Müdürü', 'Finans', 'Muhasebe', 'Accounting', 'Mali İşler'],
  },
  {
    label: 'İnsan Kaynakları',
    emoji: '🤝',
    titles: ['HR Manager', 'Human Resources', 'İnsan Kaynakları', 'İK Müdürü', 'People Operations'],
  },
];

const ApolloContactModal: React.FC<Props> = ({ show, onClose }) => {
  const [companyQuery, setCompanyQuery] = useState('');
  const [provider, setProvider] = useState<Provider>('apollo');
  const prov = PROVIDERS.find((pp) => pp.key === provider)!;
  // RocketReach'e özel opsiyonel konum filtresi
  const [locationFilter, setLocationFilter] = useState('');
  const [orgs, setOrgs] = useState<IOrg[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSearched, setOrgSearched] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<IOrg | null>(null);
  const [people, setPeople] = useState<IPerson[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [limitedMode, setLimitedMode] = useState(false);
  const [presetIdx, setPresetIdx] = useState(0);
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(new Set());

  const searchOrganizations = async (q: string, prv: Provider = provider) => {
    const query = (q || '').trim();
    if (query.length < 2) {
      toast.warning('Aramak için en az 2 karakter girin.', { autoClose: 2000 });
      return;
    }
    setOrgLoading(true);
    setOrgSearched(true);
    setSelectedOrg(null);
    setPeople([]);
    setPagination(null);
    try {
      const res = await axios.post(`${API}/${prv}/organizations/search`, { q: query });
      setOrgs(res.data?.organizations || []);
    } catch (err: any) {
      setOrgs([]);
      toast.error(err?.response?.data?.message || 'Firma araması başarısız oldu.', { autoClose: 3500 });
    } finally {
      setOrgLoading(false);
    }
  };

  const changeProvider = (p: Provider) => {
    if (p === provider) return;
    setProvider(p);
    setOrgs([]);
    setOrgSearched(false);
    setSelectedOrg(null);
    setPeople([]);
    setPagination(null);
    setLimitedMode(false);
    setPresetIdx(0);
    setLocationFilter('');
    if (companyQuery.trim().length >= 2) searchOrganizations(companyQuery, p);
  };

  const fetchPeople = async (org: IOrg, page: number, titles: string[], append: boolean, locationOverride?: string) => {
    if (append) setLoadingMore(true);
    else setPeopleLoading(true);
    // "Temizle" senaryosunda state güncellemesi asenkron kaldığından override ile geçilir
    const loc = (locationOverride !== undefined ? locationOverride : locationFilter).trim();
    try {
      const res = await axios.post(`${API}/${provider}/people/search`, {
        organization_id: org.id,
        organization_name: org.name,
        ...(provider === 'rocketreach' && org.domain ? { domain: org.domain } : {}),
        ...(provider === 'rocketreach' && loc ? { location: loc } : {}),
        titles,
        page,
        per_page: 12,
      });
      const incoming: IPerson[] = res.data?.people || [];
      setLimitedMode(!!res.data?.limited);
      setPagination(res.data?.pagination || null);
      setPeople((prev) => {
        if (!append) return incoming;
        const known = new Set(prev.map((p) => p.id));
        return [...prev, ...incoming.filter((p) => !known.has(p.id))];
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Çalışan araması başarısız oldu.', { autoClose: 3500 });
    } finally {
      setPeopleLoading(false);
      setLoadingMore(false);
    }
  };

  const selectOrg = (o: IOrg) => {
    setSelectedOrg(o);
    setPresetIdx(0);
    setPeople([]);
    fetchPeople(o, 1, [], false);
  };

  const changePreset = (idx: number) => {
    setPresetIdx(idx);
    if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[idx].titles, false);
  };

  const enrichPerson = async (person: IPerson) => {
    setEnrichingIds((s) => new Set(s).add(person.id));
    try {
      // RocketReach sonucu geç hazırlarsa yardımcı /people/enrich/status ucunu yoklar
      const enriched = await enrichPersonWithPolling<IPerson>(
        provider,
        {
          id: person.id,
          first_name: person.first_name,
          last_name: person.last_name,
          organization_name: selectedOrg?.name || person.organization_name,
          domain: selectedOrg?.domain || undefined,
        },
        (info) => toast.info(info.message || `${prov.label} kişi bilgisini hazırlıyor, bekleniyor…`, { autoClose: 4000 }),
      );
      setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, ...enriched, id: p.id, obfuscated: false, email_locked: !enriched.email } : p)));
      if (enriched?.email) {
        toast.success('Kişi bilgileri ve e-posta getirildi! 🎉', { autoClose: 1800 });
      } else {
        toast.warning(`Kişi bilgileri getirildi ancak ${prov.label} e-posta bulamadı.`, { autoClose: 2500 });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'E-posta getirilemedi.', { autoClose: 4000 });
    } finally {
      setEnrichingIds((s) => {
        const n = new Set(s);
        n.delete(person.id);
        return n;
      });
    }
  };

  const handleClose = () => {
    setCompanyQuery('');
    setLocationFilter('');
    setOrgs([]);
    setOrgSearched(false);
    setSelectedOrg(null);
    setPeople([]);
    setPagination(null);
    setLimitedMode(false);
    setPresetIdx(0);
    setEnrichingIds(new Set());
    onClose();
  };

  if (!show) return null;

  const hasMore = pagination ? (pagination.page || 1) < (pagination.total_pages || 1) : false;

  return (
    <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" style={{ maxWidth: 960 }}>
        <div className="modal-content" style={{ borderRadius: 16, overflow: 'hidden' }}>
          {/* Header */}
          <div className="modal-header border-0 px-8 pt-6 pb-0">
            <div>
              <h4 className="modal-title fw-bolder d-flex align-items-center mb-1">
                <KTSVG path="/media/icons/duotone/Communication/Address-card.svg" className="svg-icon-1 text-info me-3" />
                İletişim Bilgisi Sorgula
              </h4>
              <div className="fs-7 text-muted">
                Firma adını yazıp çalışan iletişim bilgilerini sorgulayın.
                <span className="badge badge-light-warning ms-2 fs-9">Yalnızca sorgu — kayıt yapılmaz</span>
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-icon btn-active-light-primary" onClick={handleClose}>
              <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1" />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body px-8 py-6">
            {/* Veri sağlayıcı seçimi */}
            <div className="d-flex gap-2 mb-4">
              {PROVIDERS.map((pm) => (
                <button key={pm.key} type="button" className={`btn btn-sm ${pm.key === provider ? 'btn-info' : 'btn-light'}`} onClick={() => changeProvider(pm.key)}>
                  {pm.emoji} {pm.label}
                </button>
              ))}
            </div>

            {/* Firma arama */}
            <div className="mb-6">
              <label className="form-label fw-semibold fs-6">Firma Adı</label>
              <div className="d-flex gap-3">
                <div className="position-relative flex-grow-1">
                  <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-2 svg-icon-gray-500 position-absolute top-50 translate-middle-y ms-4" />
                  <input
                    type="text"
                    className="form-control form-control-solid ps-13"
                    placeholder="Örn: Arçelik, Türk Telekom, Migros..."
                    value={companyQuery}
                    onChange={(e) => setCompanyQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') searchOrganizations(companyQuery);
                    }}
                    autoFocus
                  />
                </div>
                <button className="btn btn-info d-flex align-items-center flex-shrink-0 px-5" onClick={() => searchOrganizations(companyQuery)} disabled={orgLoading}>
                  {orgLoading ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : (
                    <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3 me-2" />
                  )}
                  Ara
                </button>
              </div>
            </div>

            {/* Firma sonuçları */}
            {orgSearched && !selectedOrg && (
              <div className="mb-2">
                {orgLoading ? (
                  <div className="text-center py-8">
                    <span className="spinner-border text-info mb-3"></span>
                    <div className="text-muted fs-7">Firmalar aranıyor...</div>
                  </div>
                ) : orgs.length > 0 ? (
                  <>
                    <div className="text-muted fs-8 fw-bold text-uppercase mb-3">Eşleşen Firmalar ({orgs.length})</div>
                    <div className="d-flex flex-column gap-2">
                      {orgs.map((o) => (
                        <div
                          key={o.id}
                          className="d-flex align-items-center p-3 rounded border border-gray-200 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onClick={() => selectOrg(o)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fa')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                        >
                          {o.logo_url ? (
                            <img src={o.logo_url} alt="" width={40} height={40} className="rounded me-3" style={{ objectFit: 'contain', background: '#fff' }} />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center rounded me-3 bg-light-info text-info fw-bold" style={{ width: 40, height: 40 }}>
                              {o.name?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <div className="fw-bold text-gray-800 fs-7">{o.name}</div>
                            <div className="text-muted fs-8 d-flex flex-wrap gap-2">
                              {o.industry && <span>{o.industry}</span>}
                              {o.estimated_num_employees != null && <span>· {o.estimated_num_employees} çalışan</span>}
                              {(o.city || o.country) && <span>· {[o.city, o.country].filter(Boolean).join(', ')}</span>}
                            </div>
                          </div>
                          <KTSVG path="/media/icons/duotone/Navigation/Arrow-right.svg" className="svg-icon-2 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted fs-7">Bu isimle eşleşen firma bulunamadı.</div>
                )}
              </div>
            )}

            {/* Seçilen firma + çalışanlar */}
            {selectedOrg && (
              <>
                <div className="d-flex align-items-center p-3 rounded mb-5" style={{ background: '#f1faff', border: '1px solid #009ef733' }}>
                  {selectedOrg.logo_url ? (
                    <img src={selectedOrg.logo_url} alt="" width={44} height={44} className="rounded me-3" style={{ objectFit: 'contain', background: '#fff' }} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center rounded me-3 bg-light-info text-info fw-bold fs-4" style={{ width: 44, height: 44 }}>
                      {selectedOrg.name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <div className="fw-bolder text-gray-800">{selectedOrg.name}</div>
                    <div className="text-muted fs-8 d-flex flex-wrap gap-2">
                      {selectedOrg.industry && <span>{selectedOrg.industry}</span>}
                      {selectedOrg.phone && <span>· 📞 {selectedOrg.phone}</span>}
                      {selectedOrg.website_url && (
                        <a href={selectedOrg.website_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          🌐 {selectedOrg.domain || 'Web sitesi'}
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-light-info"
                    onClick={() => {
                      setSelectedOrg(null);
                      setPeople([]);
                      setPagination(null);
                    }}
                  >
                    ← Firma değiştir
                  </button>
                </div>

                {/* Ünvan filtreleri */}
                <div className="d-flex flex-wrap gap-2 mb-5">
                  {TITLE_PRESETS.map((p, idx) => (
                    <button key={p.label} className={`btn btn-sm ${idx === presetIdx ? 'btn-info' : 'btn-light'}`} onClick={() => changePreset(idx)} disabled={peopleLoading}>
                      <span className="me-1">{p.emoji}</span>
                      {p.label}
                    </button>
                  ))}
                </div>

                {/* Konum filtresi (RocketReach — opsiyonel) */}
                {provider === 'rocketreach' && (
                  <div className="d-flex gap-2 align-items-center flex-wrap mb-5">
                    <input
                      type="text"
                      className="form-control form-control-solid form-control-sm"
                      style={{ maxWidth: 300 }}
                      placeholder="📍 Konum filtresi — opsiyonel (ör. Istanbul)"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false);
                        }
                      }}
                      disabled={peopleLoading}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-light-info"
                      disabled={peopleLoading}
                      onClick={() => selectedOrg && fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false)}
                    >
                      📍 Uygula
                    </button>
                    {locationFilter.trim() ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        disabled={peopleLoading}
                        onClick={() => {
                          setLocationFilter('');
                          if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false, '');
                        }}
                      >
                        ✕ Temizle
                      </button>
                    ) : null}
                  </div>
                )}

                {peopleLoading ? (
                  <div className="text-center py-8">
                    <span className="spinner-border text-info mb-3"></span>
                    <div className="text-muted fs-7">Çalışanlar getiriliyor...</div>
                  </div>
                ) : people.length > 0 ? (
                  <>
                    {limitedMode && (
                      <div className="alert alert-light-warning d-flex align-items-center py-3 px-4 mb-4 fs-8">
                        <KTSVG path="/media/icons/duotone/Code/Info-circle.svg" className="svg-icon-4 text-warning me-2" />
                        Önizleme modu — tam ad ve e-posta için "Bilgileri Getir" butonunu kullanın.
                      </div>
                    )}
                    <div className="row g-4">
                      {people.map((person) => {
                        const enriching = enrichingIds.has(person.id);
                        return (
                          <div className="col-md-6" key={person.id}>
                            <div className="d-flex flex-column gap-2 p-4 rounded border border-gray-200 h-100">
                              {/* Üst satır: avatar + ad/ünvan + LinkedIn */}
                              <div className="d-flex align-items-center">
                                {person.photo_url ? (
                                  <img src={person.photo_url} alt="" width={44} height={44} className="rounded-circle me-3 flex-shrink-0" style={{ objectFit: 'cover' }} />
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center rounded-circle me-3 bg-light-primary text-primary fw-bold flex-shrink-0"
                                    style={{ width: 44, height: 44 }}
                                  >
                                    {(person.first_name || person.name || '?').charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className="flex-grow-1 overflow-hidden">
                                  <div className="fw-bold text-gray-800 fs-7 text-truncate">
                                    {person.obfuscated ? `${person.first_name || ''} ••••••` : person.name || `${person.first_name} ${person.last_name}`}
                                  </div>
                                  {person.title && (
                                    <div className="text-muted fs-8 text-truncate" title={person.title}>
                                      {person.title}
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap gap-1 mt-1">
                                    {person.seniority && <span className="badge badge-light fs-9">{person.seniority}</span>}
                                    {person.city && <span className="badge badge-light fs-9">📍 {person.city}</span>}
                                  </div>
                                </div>
                                {person.linkedin_url && (
                                  <a
                                    href={person.linkedin_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="LinkedIn profili"
                                    onClick={(e) => e.stopPropagation()}
                                    className="ms-2 flex-shrink-0"
                                    style={{
                                      background: '#eef6fb',
                                      color: '#0a66c2',
                                      width: 32,
                                      height: 32,
                                      borderRadius: 9,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 800,
                                      fontSize: 13,
                                      textDecoration: 'none',
                                    }}
                                  >
                                    in
                                  </a>
                                )}
                              </div>

                              {/* E-posta */}
                              {person.email ? (
                                <a
                                  href={`mailto:${person.email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="d-flex align-items-center rounded px-3 py-2 fw-semibold fs-8 mt-auto"
                                  style={{ background: '#e9f9f1', border: '1px solid #12b76a33', color: '#0b7a4b', minWidth: 0, textDecoration: 'none' }}
                                >
                                  <span className="me-2">✉️</span>
                                  <span className="text-truncate" title={person.email}>
                                    {person.email}
                                  </span>
                                </a>
                              ) : person.obfuscated || person.email_locked ? (
                                <button className="btn btn-sm btn-light-info w-100 fs-8 mt-auto" onClick={() => enrichPerson(person)} disabled={enriching}>
                                  {enriching ? (
                                    <span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }}></span>
                                  ) : (
                                    <KTSVG path="/media/icons/duotone/General/Lock.svg" className="svg-icon-6 me-1" />
                                  )}
                                  Bilgileri Getir
                                </button>
                              ) : (
                                <div className="d-flex align-items-center rounded px-3 py-2 fs-8 text-muted mt-auto" style={{ background: '#f5f8fa' }}>
                                  <span className="me-2">✉️</span>E-posta bulunamadı
                                </div>
                              )}

                              {/* Telefon */}
                              {(person.organization_phone || selectedOrg.phone) && (
                                <div className="d-flex align-items-center rounded px-3 py-2 fs-8" style={{ background: '#f5f8fa', border: '1px solid #eff2f5' }}>
                                  <span className="me-2">📞</span>
                                  <span className="fw-semibold text-gray-800 text-truncate flex-grow-1">{person.organization_phone || selectedOrg.phone}</span>
                                  <span className="text-muted fs-9 ms-2 flex-shrink-0">
                                    {provider === 'rocketreach' && person.organization_phone ? 'direkt tel.' : 'firma tel.'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {hasMore && (
                      <div className="text-center mt-5">
                        <button
                          className="btn btn-light-info"
                          onClick={() => selectedOrg && fetchPeople(selectedOrg, (pagination?.page || 1) + 1, TITLE_PRESETS[presetIdx].titles, true)}
                          disabled={loadingMore}
                        >
                          {loadingMore ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                          Daha fazla göster
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-muted fs-7">Bu firmada seçilen kritere uygun çalışan bulunamadı.</div>
                )}
              </>
            )}

            {!orgSearched && (
              <div className="d-flex flex-column align-items-center text-center py-10 text-muted">
                <KTSVG path="/media/icons/duotone/Communication/Address-card.svg" className="svg-icon-3x text-gray-300 mb-3" />
                <div className="fs-7">Sorgulamak istediğiniz firmanın adını yazıp "Ara" deyin.</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 px-8 pb-6 pt-2">
            <button className="btn btn-light" onClick={handleClose}>
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApolloContactModal;
